import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DocumentPdfViewerComponent } from '../document-pdf-viewer/document-pdf-viewer.component';
import {
  DwComponent, DwComponentType, DwExpressionService, DwFormContextMode, DwMdFormContextInfo, DwMetaDataFormApi, DwMetaDataFormStateService,
  DwMetaDataService, DwMetaDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken,
  DwLookupSearchType, DwOrmDataService, DwOrmDataServiceToken, DwQueryHelper, DwQueryFilter, DwOperator, DwFilterType,
  isAfterDate,
  DwMediaRepositoryService,
  DwLoggingService
}
  from '@devwareapps/devware-cap';
import { AppMetaDataItemNames, DocumentItemEntity, DocumentTypeAllItems } from '../../../../meta-data/app-meta-data.service';
import { StructuredDocumentSectionReferenceModel, StructuredDocumentViewerModel } from '../../structured-content/models/structured-document-viewer.model';
import { StructuredDocumentViewerService } from '../../structured-content/services/structured-document-viewer.service';
import { catchError, take } from 'rxjs/operators';
import { DocumentsRepositoryService } from '../../services/documents-repository.service';
import { DocumentViewerTraceService } from '../../services/document-viewer-trace.service';
import { of } from 'rxjs';
import { finalize } from 'rxjs/operators';

@DwComponent({
  key: 'document-viewer',
  name: 'Document Viewer',
  componentType: DwComponentType.formSection,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.DocumentItem
})
@Component({
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss']
})
export class DocumentViewerComponent extends DwSectionBaseComponent implements OnInit, OnChanges {
  @Input() documentItem: DocumentItemEntity;
  @Input() documentItemId?: number;
  @Input() hideHeader: boolean = false;
  @Input() initialPageNumber?: number;
  @Input() pageNumbers?: string;
  @Input() documentVersionId?: number;
  @Input() structuredDocumentReferences: StructuredDocumentSectionReferenceModel[] = [];

  // When a host listens for (close), the close / back button delegates
  // to the host instead of running `location.back()`. Lets the viewer
  // be embedded (e.g. in the lesson study companion) without yanking
  // the user out of the surrounding page.
  @Output() close = new EventEmitter<void>();

  @ViewChild(DocumentPdfViewerComponent) pdfViewer?: DocumentPdfViewerComponent;
  @ViewChild('searchInputRef') searchInputRef?: ElementRef<HTMLInputElement>;

  documentUrl: string = '';
  formApi: DwMetaDataFormApi;
  isFullscreen: boolean;
  currentPage: number = 0;
  totalPages: number = 0;

  searchOpen: boolean = false;
  searchQuery: string = '';
  matchCount: { current: number; total: number } = { current: 0, total: 0 };

  currentScale: number = 1;

  get zoomPercent(): number {
    return Math.round(this.currentScale * 100);
  }

  highlightModeOn: boolean = false;

  kebabOpen: boolean = false;

  get progressPercent(): number {
    return this.totalPages > 0 ? (this.currentPage / this.totalPages) * 100 : 0;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  onPagesLoaded(total: number): void {
    this.totalPages = total;
  }

  onSearchMatchCount(count: { current: number; total: number }): void {
    this.matchCount = count;
  }

  onScaleChange(scale: number): void {
    this.currentScale = scale;
  }

  toggleHighlightMode(): void {
    this.highlightModeOn = !this.highlightModeOn;
    this.pdfViewer?.setHighlightMode(this.highlightModeOn);
  }

  toggleKebab(): void {
    this.kebabOpen = !this.kebabOpen;
  }

  closeKebab(): void {
    this.kebabOpen = false;
  }

  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      // focus the input on next tick so the *ngIf has materialized the element
      setTimeout(() => this.searchInputRef?.nativeElement.focus(), 0);
    } else {
      this.clearSearch();
    }
  }

  onSearchInput(query: string): void {
    this.searchQuery = query;
    this.pdfViewer?.searchInDocument(query);
  }

  findPrevious(): void {
    if (!this.searchQuery) return;
    this.pdfViewer?.searchInDocument(this.searchQuery, { findPrevious: true });
  }

  findNext(): void {
    if (!this.searchQuery) return;
    this.pdfViewer?.searchInDocument(this.searchQuery, { findPrevious: false });
  }

  closeSearch(): void {
    this.searchOpen = false;
    this.clearSearch();
  }

  private clearSearch(): void {
    this.searchQuery = '';
    this.matchCount = { current: 0, total: 0 };
    // dispatch an empty find to clear pdfjs's highlight state
    this.pdfViewer?.searchInDocument('', { highlightAll: false });
  }


  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
    @Optional()private dwFormStateService: DwMetaDataFormStateService,
    private dwMediaRepositoryService: DwMediaRepositoryService,
    private dwLoggingService: DwLoggingService,
    private structuredDocumentViewerService: StructuredDocumentViewerService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private documentsRepositoryService: DocumentsRepositoryService,
    private readonly viewerTrace: DocumentViewerTraceService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService?.state?.formApi;
    this.traceViewerId = this.viewerTrace.nextViewerId();
    this.viewerTrace.observeForm(this.formApi);
    this.traceViewer('viewer.created');
  }

  ngOnInit(): void {
    this.traceViewer('viewer.init');
    this.refreshPageNumberChips();
    this.setupDocumentUrl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.traceViewer('viewer.inputs-changed', true);
    if (changes.documentItem || changes.documentItemId) {
      this.hasDocumentInput = true;
      this.documentLoadGeneration++;
      this.loadingDocumentItemId = undefined;
    }

    if (!changes.documentItem && !changes.documentItemId && !changes.initialPageNumber && !changes.pageNumbers) {
      return;
    }

    if (changes.pageNumbers) {
      this.refreshPageNumberChips();
    }

    this.resetViewerState();
    this.setupDocumentUrl();
  }

  setupComponentComplete(): void {
    this.formApi = this.dwFormStateService?.state?.formApi || this.formApi;
    this.viewerTrace.observeForm(this.formApi);
    this.traceViewer('viewer.setup-complete', true);
    if (!this.hasDocumentInput && this.formApi?.item === AppMetaDataItemNames.DocumentItem
      && this.observedFormApi !== this.formApi) {
      this.observedFormApi = this.formApi;
      this.subscriptions.push(this.formApi.isDataLoadComplete.subscribe(loaded => {
        this.traceViewer(loaded ? 'viewer.form-ready' : 'viewer.form-pending', true);
        if (loaded && !this.hasDocumentInput) {
          this.documentLoadGeneration++;
          this.loadingDocumentItemId = undefined;
          this.resetViewerState();
          this.documentItem = this.formApi.getFormData<DocumentItemEntity>();
          this.setupDocumentUrl();
        }
      }));
    }
    this.runAfterFormDataLoaded(() => {
      this.refreshPageNumberChips();
      this.setupDocumentUrl();
    });
  }

  
  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
  }

  goBack() {
    // EventEmitter.observed isn't available on Angular 10 (RxJS 6).
    // observers.length is the equivalent and works back to Angular 2.
    if (this.close.observers.length > 0) {
      this.close.emit();
      return;
    }
    this.location.back();
  }

  showPDFViewer: boolean;
  showEmbeddedViewer: boolean;
  showStructuredViewer: boolean;
  structuredViewerData: StructuredDocumentViewerModel | null = null;
  structuredViewerError: string | null = null;
  isStructuredViewerLoading: boolean = false;
  readonly structuredSkeletonNavGroups = [
    { parts: [0, 1] },
    { parts: [0, 1, 2] },
  ];
  pageNumberChips: DocumentPageJumpChip[] = [];
  private lastLoadedDocumentItemId?: number;
  private hasDocumentInput = false;
  private documentLoadGeneration = 0;
  private loadingDocumentItemId?: number;
  private observedFormApi?: DwMetaDataFormApi;
  private readonly traceViewerId: number;
  isDocumentLoading = true;
  documentLoadError: string | null = null;

  retryDocument(): void {
    this.loadingDocumentItemId = undefined;
    this.documentLoadGeneration++;
    this.resetViewerState();
    const documentItemId = this.getDocumentItemId();
    if (documentItemId) {
      this.loadDocumentItem(documentItemId);
    } else {
      this.setupDocumentUrl();
    }
  }

  get resolvedInitialPageNumber(): number | undefined {
    if (this.initialPageNumber && this.initialPageNumber > 0) {
      return this.initialPageNumber;
    }

    const routePageValue = this.activatedRoute?.snapshot?.queryParamMap?.get('page');
    const parsedPageValue = routePageValue ? parseInt(routePageValue, 10) : null;

    return parsedPageValue && parsedPageValue > 0 ? parsedPageValue : undefined;
  }

  jumpToPageRange(pageNumber: number): void {
    this.pdfViewer?.goToPage(pageNumber);
  }

  trackByPageJumpChip(index: number, chip: DocumentPageJumpChip): number {
    return chip.firstPageNumber;
  }
  
  
  private setupDocumentUrl() {
    this.traceViewer('viewer.resolve');

    // An embedded lesson's form contains CourseLesson, not DocumentItem.
    // Never use the parent form as a fallback for an explicitly bound input.
    this.formApi = this.dwFormStateService?.state?.formApi || this.formApi;
    const isDocumentForm = !this.hasDocumentInput && this.formApi?.item === AppMetaDataItemNames.DocumentItem;
    if (!this.documentItem && isDocumentForm) {
      this.documentItem = this.formApi.getFormData<DocumentItemEntity>();
    }

    const documentItemId = this.getDocumentItemId();
    if (documentItemId && (this.documentItem?.DocumentItemId !== documentItemId || !this.documentItem?.DocumentTypeId
      || (this.documentItem.DocumentTypeId === DocumentTypeAllItems.SketchFab3D && !this.documentItem.EmbeddedMarkup))) {
      this.loadDocumentItem(documentItemId);
      return;
    }

    if (!this.documentItem?.DocumentItemId) {
      this.isDocumentLoading = !!isDocumentForm && !this.formApi.isDataLoadComplete.value;
      if (!this.isDocumentLoading) {
        this.documentLoadError = 'This resource is not available. Please try again.';
      }
      return;
    }

    if (this.lastLoadedDocumentItemId === this.documentItem.DocumentItemId
      && (this.isStructuredViewerLoading || !!this.structuredViewerData || !!this.documentUrl)) {
      return;
    }

    this.lastLoadedDocumentItemId = this.documentItem.DocumentItemId;

    this.showPDFViewer = this.documentItem?.DocumentTypeId === DocumentTypeAllItems.PDFDocument;
    this.showEmbeddedViewer = this.documentItem?.DocumentTypeId === DocumentTypeAllItems.SketchFab3D;
    this.showStructuredViewer = this.documentItem?.DocumentTypeId === DocumentTypeAllItems.FARAIM;
    this.isDocumentLoading = false;
    this.documentLoadError = this.showPDFViewer || this.showEmbeddedViewer || this.showStructuredViewer
      ? null : 'This resource type cannot be displayed.';

    if (this.showStructuredViewer) {
      this.isStructuredViewerLoading = true;
      this.structuredDocumentViewerService.getStructuredDocumentViewer(this.documentItem.DocumentItemId, this.documentVersionId)
        .pipe(finalize(() => {
          this.isStructuredViewerLoading = false;
        }))
        .pipe(catchError(error => {
          this.structuredViewerError = this.getStructuredViewerErrorMessage(error);
          return of(null);
        }))
        .subscribe(structuredViewer => {
          if (structuredViewer?.DocumentVersionId) {
            this.structuredViewerData = structuredViewer;
            this.structuredViewerError = null;
            return;
          }

          if (!this.structuredViewerError) {
            this.structuredViewerError = 'Structured document content is not available for this FAR/AIM document yet.';
          }
        });

      return;
    }

    if (this.showPDFViewer && this.documentItem?.MediaId) {
      this.dwMediaRepositoryService.loadMedia(this.documentItem.MediaId)
        .subscribe(media => {
          if (this.showPDFViewer) {
           //this.dwLoggingService.logVerbose(`DocumentViewerComponent: Viewing PDF "${this.documentItem.DocumentItemName}" (${this.documentItem.DocumentItemId}) - MediaGuid ${media[0].FileName}`, null, true);
          }

          this.documentUrl = this.dwMediaRepositoryService.getImageUrlFromMediaPath(media[0].MediaPath);
        });
    }
  }

  private getDocumentItemId(): number | undefined {
    const value = this.documentItemId
      || (!this.hasDocumentInput && this.formApi?.item === AppMetaDataItemNames.DocumentItem ? this.formApi.keyValue : undefined)
      || this.documentItem?.DocumentItemId;
    const documentItemId = Number(value);
    return Number.isInteger(documentItemId) && documentItemId > 0 ? documentItemId : undefined;
  }

  private loadDocumentItem(documentItemId: number): void {
    if (this.loadingDocumentItemId === documentItemId) {
      return;
    }

    this.loadingDocumentItemId = documentItemId;
    this.traceViewer('viewer.repository-load', true);
    this.isDocumentLoading = true;
    this.documentLoadError = null;
    const generation = this.documentLoadGeneration;
    this.subscriptions.push(this.documentsRepositoryService.loadDocumentItem(documentItemId)
      .pipe(take(1), finalize(() => {
        if (generation === this.documentLoadGeneration) {
          this.loadingDocumentItemId = undefined;
        }
      }))
      .subscribe(documentItem => {
        this.viewerTrace.record('viewer.repository-result', {
          viewerId: this.traceViewerId, documentItemId,
          responseType: documentItem?.DocumentTypeId,
          hasMarkup: !!documentItem?.EmbeddedMarkup,
          stale: this.destroyed || generation !== this.documentLoadGeneration
        });
        if (this.destroyed || generation !== this.documentLoadGeneration) {
          return;
        }
        if (documentItem?.DocumentItemId === documentItemId && documentItem.DocumentTypeId
          && (documentItem.DocumentTypeId !== DocumentTypeAllItems.SketchFab3D || !!documentItem.EmbeddedMarkup)) {
          this.documentItem = documentItem;
          this.setupDocumentUrl();
        } else {
          this.isDocumentLoading = false;
          this.documentLoadError = 'This resource does not have content available yet.';
        }
      }, error => {
        if (!this.destroyed && generation === this.documentLoadGeneration) {
          this.isDocumentLoading = false;
          this.documentLoadError = 'Unable to load this resource. Please try again.';
          this.dwLoggingService.logError('Unable to load document viewer data.', error);
        }
      }));
  }

  private resetViewerState(): void {
    this.traceViewer('viewer.reset', true);
    this.isDocumentLoading = true;
    this.documentLoadError = null;
    this.documentUrl = '';
    this.showPDFViewer = false;
    this.showEmbeddedViewer = false;
    this.showStructuredViewer = false;
    this.structuredViewerData = null;
    this.structuredViewerError = null;
    this.isStructuredViewerLoading = false;
    this.lastLoadedDocumentItemId = undefined;
    this.currentPage = 0;
    this.totalPages = 0;
    this.currentScale = 1;
    this.highlightModeOn = false;
    this.kebabOpen = false;
    this.closeSearch();
  }

  ngOnDestroy(): void {
    this.traceViewer('viewer.destroyed', true);
    super.ngOnDestroy();
  }

  private traceViewer(event: string, includeStack = false): void {
    this.viewerTrace.record(event, {
      viewerId: this.traceViewerId,
      documentItemId: this.documentItemId || this.documentItem?.DocumentItemId,
      documentTypeId: this.documentItem?.DocumentTypeId,
      hasMarkup: !!this.documentItem?.EmbeddedMarkup,
      formItem: this.formApi?.item,
      formLoaded: !!this.formApi?.isDataLoadComplete?.value,
      embeddedVisible: !!this.showEmbeddedViewer,
      pdfVisible: !!this.showPDFViewer
    }, includeStack);
  }

  private buildPageNumberChips(pageNumbers?: string): DocumentPageJumpChip[] {
    if (!pageNumbers) {
      return [];
    }

    const uniqueSortedPages = Array.from(new Set(
      pageNumbers
        .split(',')
        .map(pageText => parseInt(pageText.trim(), 10))
        .filter(pageNumber => !isNaN(pageNumber) && pageNumber > 0)
    )).sort((a, b) => a - b);

    if (!uniqueSortedPages.length) {
      return [];
    }

    const chips: DocumentPageJumpChip[] = [];
    let rangeStart = uniqueSortedPages[0];
    let previousPage = uniqueSortedPages[0];

    for (let index = 1; index <= uniqueSortedPages.length; index++) {
      const currentPage = uniqueSortedPages[index];
      const rangeContinues = currentPage === previousPage + 1;

      if (rangeContinues) {
        previousPage = currentPage;
        continue;
      }

      chips.push({
        firstPageNumber: rangeStart,
        label: rangeStart === previousPage ? `Page ${rangeStart}` : `Pages ${rangeStart}-${previousPage}`
      });

      rangeStart = currentPage;
      previousPage = currentPage;
    }

    return chips;
  }

  private refreshPageNumberChips(): void {
    this.pageNumberChips = this.buildPageNumberChips(this.pageNumbers);
  }

  private getStructuredViewerErrorMessage(error: any): string {
    if (error?.status === 404) {
      return 'Structured document content has not been loaded for this FAR/AIM document yet.';
    }

    return 'There was a problem loading the FAR/AIM content. Please try again later.';
  }

}

interface DocumentPageJumpChip {
  firstPageNumber: number;
  label: string;
}
